/**
 * usePendingDeletes - Tracks images marked for deletion and replacements.
 *
 * Tracks by link_id (stable across reorders).
 * Supports replacements: delete old image + link new image on same slot.
 * On save: DELETE old slot, then POST new image to slot (sequential).
 */

import { useState, useCallback, useMemo } from 'react';

export interface PendingDeleteEntry {
  channelId: string;
  linkId: string;
  slot: number;
  isCascade: boolean;
}

export interface PendingReplacement {
  channelId: string;
  slot: number;
  oldLinkId: string;
  newAssetId: string;
  newWidth: number;
  newHeight: number;
}

export function usePendingDeletes() {
  const [pendingDeletes, setPendingDeletes] = useState<PendingDeleteEntry[]>([]);
  const [pendingReplacements, setPendingReplacements] = useState<PendingReplacement[]>([]);

  const markForDeletion = useCallback((channelId: string, linkId: string, slot: number, isCascade = false) => {
    setPendingDeletes((prev) => {
      if (prev.some((d) => d.channelId === channelId && d.linkId === linkId)) return prev;
      return [...prev, { channelId, linkId, slot, isCascade }];
    });
  }, []);

  const unmarkDeletion = useCallback((channelId: string, linkId: string) => {
    setPendingDeletes((prev) => prev.filter((d) => !(d.channelId === channelId && d.linkId === linkId)));
    setPendingReplacements((prev) => prev.filter((r) => !(r.channelId === channelId && r.oldLinkId === linkId)));
  }, []);

  const clearAllDeletes = useCallback(() => {
    setPendingDeletes([]);
    setPendingReplacements([]);
  }, []);

  const isPendingDelete = useCallback(
    (channelId: string, linkId: string) => pendingDeletes.some((d) => d.channelId === channelId && d.linkId === linkId),
    [pendingDeletes],
  );

  const isCascadeDelete = useCallback(
    (channelId: string, linkId: string) => pendingDeletes.some((d) => d.channelId === channelId && d.linkId === linkId && d.isCascade),
    [pendingDeletes],
  );

  /** Check if a pending-delete slot has a replacement image queued */
  const getReplacement = useCallback(
    (channelId: string, oldLinkId: string) => pendingReplacements.find((r) => r.channelId === channelId && r.oldLinkId === oldLinkId),
    [pendingReplacements],
  );

  /** Convert a pending-delete to a replacement (delete old + link new on same slot) */
  const addReplacement = useCallback((channelId: string, oldLinkId: string, slot: number, newAssetId: string, newWidth: number, newHeight: number) => {
    setPendingReplacements((prev) => {
      const filtered = prev.filter((r) => !(r.channelId === channelId && r.oldLinkId === oldLinkId));
      return [...filtered, { channelId, slot, oldLinkId, newAssetId, newWidth, newHeight }];
    });
  }, []);

  const pendingDeleteCount = useMemo(() => pendingDeletes.length, [pendingDeletes]);

  return {
    pendingDeletes, pendingReplacements,
    markForDeletion, unmarkDeletion, clearAllDeletes,
    isPendingDelete, isCascadeDelete, getReplacement, addReplacement,
    pendingDeleteCount,
  };
}
